// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, TDataShape, Client } from './client';
import type { GetArticleByDateData, GetArticleByDateResponses, GetArticleByDateErrors, GetLatestArticleData, GetLatestArticleResponses, GetLatestArticleErrors, GetTemperatureMapListData, GetTemperatureMapListResponses, GetGeoLocationByIdData, GetGeoLocationByIdResponses, GetGeoLocationByIdErrors, GetGeoLocationsData, GetGeoLocationsResponses, GetGeoLocationNamesData, GetGeoLocationNamesResponses, GetGeoLocationNamesErrors, GetSymbolCodesData, GetSymbolCodesResponses, GetFavoritesData, GetFavoritesResponses, GetFavoritesErrors, PatchFavoriteData, PatchFavoriteResponses, PatchFavoriteErrors, StoreFavoriteData, StoreFavoriteResponses, StoreFavoriteErrors, ReplaceFavoriteData, ReplaceFavoriteResponses, ReplaceFavoriteErrors, DeleteFavoriteData, DeleteFavoriteResponses, DeleteFavoriteErrors, GetForecastWeekByGeoLocationData, GetForecastWeekByGeoLocationResponses, GetForecastWeekByGeoLocationErrors, GetLatestRegionalSummaryData, GetLatestRegionalSummaryResponses, GetLatestRegionalSummaryErrors, GetSurfWeatherWeekBySpotIdData, GetSurfWeatherWeekBySpotIdResponses, GetSurfWeatherWeekBySpotIdErrors, GetWarningsData, GetWarningsResponses, GetWarningByIdData, GetWarningByIdResponses, GetWarningByIdErrors, GetWarningByIdsData, GetWarningByIdsResponses, GetWarningByIdsErrors } from './types.gen';
import { client as _heyApiClient } from './client.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<TData, ThrowOnError> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Returns articles by date
 */
export const getArticleByDate = <ThrowOnError extends boolean = false>(options: Options<GetArticleByDateData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetArticleByDateResponses, GetArticleByDateErrors, ThrowOnError>({
        url: '/articles/{year}/{month}/{day}',
        ...options
    });
};

/**
 * Returns the latest article
 */
export const getLatestArticle = <ThrowOnError extends boolean = false>(options?: Options<GetLatestArticleData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetLatestArticleResponses, GetLatestArticleErrors, ThrowOnError>({
        url: '/articles/latest',
        ...options
    });
};

/**
 * Returns the complete list of temperature-color mappings
 * Returns the complete list of current temperature-color mappings.  Needed to accurately display the accurate color value in GUIs
 */
export const getTemperatureMapList = <ThrowOnError extends boolean = false>(options?: Options<GetTemperatureMapListData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetTemperatureMapListResponses, unknown, ThrowOnError>({
        url: '/colors',
        ...options
    });
};

/**
 * Returns a single geolocation object including its geolocationNames
 */
export const getGeoLocationById = <ThrowOnError extends boolean = false>(options: Options<GetGeoLocationByIdData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetGeoLocationByIdResponses, GetGeoLocationByIdErrors, ThrowOnError>({
        url: '/geolocations/{geolocationId}',
        ...options
    });
};

/**
 * Search geolocations objects near given coordinates
 */
export const getGeoLocations = <ThrowOnError extends boolean = false>(options?: Options<GetGeoLocationsData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetGeoLocationsResponses, unknown, ThrowOnError>({
        url: '/geolocations',
        ...options
    });
};

/**
 * Searches geolocationNames by given name or zip
 */
export const getGeoLocationNames = <ThrowOnError extends boolean = false>(options?: Options<GetGeoLocationNamesData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetGeoLocationNamesResponses, GetGeoLocationNamesErrors, ThrowOnError>({
        url: '/geolocationNames',
        ...options
    });
};

/**
 * Returns a complete list of all symbolcodes and descriptions
 */
export const getSymbolCodes = <ThrowOnError extends boolean = false>(options?: Options<GetSymbolCodesData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetSymbolCodesResponses, unknown, ThrowOnError>({
        url: '/symbols',
        ...options
    });
};

/**
 * Retrieve all favourites
 */
export const getFavorites = <ThrowOnError extends boolean = false>(options?: Options<GetFavoritesData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetFavoritesResponses, GetFavoritesErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/my/favorites',
        ...options
    });
};

/**
 * Update the order of favourites
 * Replaces the current order with the provided one. The full, newly sorted list of all favourite entries must be sent in the desired order.
 */
export const patchFavorite = <ThrowOnError extends boolean = false>(options?: Options<PatchFavoriteData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).patch<PatchFavoriteResponses, PatchFavoriteErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/my/favorites',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers
        }
    });
};

/**
 * Add one or more favourites
 * Appends non-existing favourites, updates existing ones. Use PATCH for ordering.
 */
export const storeFavorite = <ThrowOnError extends boolean = false>(options?: Options<StoreFavoriteData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).post<StoreFavoriteResponses, StoreFavoriteErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/my/favorites',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers
        }
    });
};

/**
 * Replaces a list of favourites
 * Replaces the entire favorite list if client's `updated` timestamp is newer or equal to the server's.
 */
export const replaceFavorite = <ThrowOnError extends boolean = false>(options: Options<ReplaceFavoriteData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).put<ReplaceFavoriteResponses, ReplaceFavoriteErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/my/favorites',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Remove a single favourite
 */
export const deleteFavorite = <ThrowOnError extends boolean = false>(options: Options<DeleteFavoriteData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).delete<DeleteFavoriteResponses, DeleteFavoriteErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/my/favorites/{geolocationId}',
        ...options
    });
};

/**
 * Returns forecast data grouped by day,3hour and hour
 * Returns forecast data grouped by day-intervals, 3hour-intervals (threeHours) and hours interval
 */
export const getForecastWeekByGeoLocation = <ThrowOnError extends boolean = false>(options: Options<GetForecastWeekByGeoLocationData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetForecastWeekByGeoLocationResponses, GetForecastWeekByGeoLocationErrors, ThrowOnError>({
        url: '/forecastpoint/{geolocationId}',
        ...options
    });
};

/**
 * Returns latest regional summary
 */
export const getLatestRegionalSummary = <ThrowOnError extends boolean = false>(options: Options<GetLatestRegionalSummaryData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetLatestRegionalSummaryResponses, GetLatestRegionalSummaryErrors, ThrowOnError>({
        url: '/regional-summary/{region}/latest',
        ...options
    });
};

/**
 * Returns surf weather data grouped by day, 3 hour and hour
 * Returns surf weather data grouped by day intervals, 3 hour intervals (threeHours) and hours interval
 */
export const getSurfWeatherWeekBySpotId = <ThrowOnError extends boolean = false>(options: Options<GetSurfWeatherWeekBySpotIdData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetSurfWeatherWeekBySpotIdResponses, GetSurfWeatherWeekBySpotIdErrors, ThrowOnError>({
        url: '/surf-weather/{spot}',
        ...options
    });
};

/**
 * Returns all currently active warnings (ordered by priority desc and start asc)
 */
export const getWarnings = <ThrowOnError extends boolean = false>(options?: Options<GetWarningsData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetWarningsResponses, unknown, ThrowOnError>({
        url: '/warning',
        ...options
    });
};

/**
 * Returns warnings for a region (ordered by priority desc and start asc)
 */
export const getWarningById = <ThrowOnError extends boolean = false>(options: Options<GetWarningByIdData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetWarningByIdResponses, GetWarningByIdErrors, ThrowOnError>({
        url: '/warning/id/{id}',
        ...options
    });
};

/**
 * Returns warnings for several regions (ordered by priority desc and start asc)
 * @deprecated
 */
export const getWarningByIds = <ThrowOnError extends boolean = false>(options: Options<GetWarningByIdsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetWarningByIdsResponses, GetWarningByIdsErrors, ThrowOnError>({
        url: '/warning/ids/{ids}',
        ...options
    });
};